// Import controller interfaces
import {
    IPolicyNoteController
} from '../Interfaces/ControllerInterfacesBarrel';
// Import api models.
import ApiController from './ApiController';
import {
    ErrorMessage,
    PolicyNoteDocumentWithData,
    PolicyNote,
    PolicyNoteEmail,
    PolicyNoteRecord,
    PossibleCorrespondenceRecvdOrSent,
    PossibleCorrespondenceSubject,
    PossibleCorrespondenceType,
    PossibleCorrespondenceWho,
} from '@app/models/api';

export default class ProductionPolicyNoteController extends ApiController implements IPolicyNoteController {

    private readonly controllerUrl: string = 'v2/policyNote';

    public constructor() {
        super();
    }

    public async getPolicyNotes(policyId: number): Promise<PolicyNoteRecord[]> {
        let url: string = `${this.controllerUrl}/policy/${policyId}`;

        try {
            let response: Response = await super.makeGetCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not get notes.';
            }
            throw error;
        }
    }

    public async savePolicyNote(policyId: number, note: string, document: PolicyNoteDocumentWithData): Promise<PolicyNoteRecord> {
        let url: string = `${this.controllerUrl}/${policyId}`;
        let data = {
            'note': note,
            'document': {
                'name': document.docName,
                'data': document.data !== null ? [].slice.call(document.data) : null,
                'category': document.docCategory,
                'year': document.docYear,
            }
        };

        try {
            let response: Response = await super.makePostCall(url, data);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not save note.';
            }
            throw error;
        }
    }

    public async savePolicyNoteAdmin(policyId: number, policyNote: PolicyNote, document: PolicyNoteDocumentWithData): Promise<PolicyNoteRecord> {
        let url: string = `${this.controllerUrl}/admin/${policyId}`;
        let data = {
            'note': policyNote.note,
            'adminOnly': policyNote.adminOnly,
            'isFlag': policyNote.isFlag,
            'flagDate': policyNote.flagDate,
            'correspondenceName': policyNote.correspondenceName,
            'correspondenceEmailAddress': policyNote.correspondenceEmailAddress,
            'correspondencePhone1': policyNote.correspondencePhone1,
            'correspondencePhone2': policyNote.correspondencePhone2,
            'correspondenceRecvdOrSent': policyNote.correspondenceRecvdOrSent,
            'correspondenceType': policyNote.correspondenceType,
            'correspondenceWho': policyNote.correspondenceWho,
            'correspondenceSubject': policyNote.correspondenceSubject,
            'correspondenceReasons': policyNote.correspondenceReasons,
            'complaintInitiated': policyNote.complaintInitiated,
            'document': {
                'name': document.docName,
                'data': document.data !== null ? [].slice.call(document.data) : null,
                'category': document.docCategory,
                'year': document.docYear,
            }
        };

        try {
            let response: Response = await super.makePostCall(url, data);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not save note.';
            }
            throw error;
        }
    }

    public async saveNoteToPolicyNote(policyNoteId: number, note: string): Promise<PolicyNoteRecord> {
        let url: string = `${this.controllerUrl}/admin/${policyNoteId}`;
        let data = note

        try {
            let response: Response = await super.makePutCall(url, data);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not add note.';
            }
            throw error;
        }
    }

    public async changeFlagDate(policyNoteId: number, flagDate: Date, note: string): Promise<PolicyNoteRecord> {
        let url: string = `${this.controllerUrl}/admin/flagDate/${policyNoteId}`;
        let data = {
            'flagDate': flagDate,
            'note': note,
        }

        try {
            let response: Response = await super.makePutCall(url, data);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not update flag date.';
            }
            throw error;
        }
    }

    public async markPolicyNoteAsComplete(policyNoteId: number, note: string): Promise<PolicyNoteRecord> {
        let url: string = `${this.controllerUrl}/admin/markComplete/${policyNoteId}`;
        let data = note;

        try {
            let response: Response = await super.makePutCall(url, data);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not mark note as complete.';
            }
            throw error;
        }
    }

    public async archivePolicyNote(policyNoteId: number): Promise<void> {
        let url: string = `${this.controllerUrl}/admin/${policyNoteId}`;

        try {
            let response: Response = await super.makeDeleteCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not archive note.';
            }
            throw error;
        }
    }

    public async getPolicyNoteEmails(policyId: number): Promise<PolicyNoteEmail[]> {
        let url: string = `${this.controllerUrl}/email/${policyId}`;

        try {
            let response: Response = await super.makeGetCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not get note and emails.';
            }
            throw error;
        }
    }

    public async getPossibleCorrespondenceRecvdOrSentOptions(): Promise<PossibleCorrespondenceRecvdOrSent[]> {
        let url: string = `${this.controllerUrl}/correspondenceRecvdOrSent`;

        try {
            let response: Response = await super.makeGetCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not get correspondence received or sent options.';
            }
            throw error;
        }
    }

    public async getPossibleCorrespondenceTypeOptions(): Promise<PossibleCorrespondenceType[]> {
        let url: string = `${this.controllerUrl}/correspondenceType`;

        try {
            let response: Response = await super.makeGetCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not get correspondence type options.';
            }
            throw error;
        }
    }

    public async getPossibleCorrespondenceWhoOptions(): Promise<PossibleCorrespondenceWho[]> {
        let url: string = `${this.controllerUrl}/correspondenceWho`;

        try {
            let response: Response = await super.makeGetCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not get correspondence who options.';
            }
            throw error;
        }
    }

    public async getPossibleCorrespondenceSubjectOptions(): Promise<PossibleCorrespondenceSubject[]> {
        let url: string = `${this.controllerUrl}/correspondenceSubject`;

        try {
            let response: Response = await super.makeGetCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not get correspondence subject options.';
            }
            throw error;
        }
    }
};